import { Slider, Stack, Typography, useTheme } from '@mui/material';
import { ArtistInfo } from '../database/user';
import { PaymentModel } from './constants/PaymentModel';
import { ResponseType } from './constants/ResponseType';
import { useState, useEffect } from 'react';
import { Mark } from '@mui/material/Slider/useSlider.types';
import Icon from '../components/Icon';

export interface CostCardProps {
    artistInfo: ArtistInfo;
    responseType: ResponseType;
    onCostSelect: (cost: number) => void;
    selectedCost: number;
}

const CostCard: React.FC<CostCardProps> = ({
    artistInfo,
    responseType,
    onCostSelect,
    selectedCost,
}) => {
    const theme = useTheme();

    useEffect(() => {
        if (artistInfo.paymentModel === PaymentModel.STANDARD) {
            if (responseType === ResponseType.Text) {
                onCostSelect(artistInfo.costTextResponseType || 0);
            } else if (responseType === ResponseType.Video) {
                onCostSelect(artistInfo.costVideoResponseType || 0);
            }
        }
    }, [artistInfo, responseType, onCostSelect]);

    const handleSliderChange = (event: Event, value: number | number[]) => {
        const cost = Array.isArray(value) ? value[0] : value;
        onCostSelect(cost);
    };

    if (!artistInfo.pwywPrice) {
        return null;
    } else {
        switch (artistInfo.paymentModel) {
            case PaymentModel.STANDARD:
                switch (responseType) {
                    case ResponseType.Text:
                        return (
                            <Stack>
                                <Stack direction='row' alignItems='center' spacing={1}>
                                    <Typography
                                        variant='subtitle1'
                                        fontFamily='LemonMilk'
                                    >
                                        Cost
                                    </Typography>
                                    <Icon
                                        fontSize='inherit'
                                        name='cost'
                                        color='primary'
                                    />
                                </Stack>
                                <Typography variant='body1' color='primary'>
                                    ${artistInfo.costTextResponseType}
                                </Typography>
                            </Stack>
                        );
                    case ResponseType.Video:
                        return (
                            <Stack>
                                <Stack direction='row' alignItems='center' spacing={1}>
                                    <Typography
                                        variant='subtitle1'
                                        fontFamily='LemonMilk'
                                    >
                                        Cost
                                    </Typography>
                                    <Icon
                                        fontSize='inherit'
                                        name='cost'
                                        color='primary'
                                    />
                                </Stack>
                                <Typography variant='body1' color='primary'>
                                    ${artistInfo.costVideoResponseType}
                                </Typography>
                            </Stack>
                        );
                }
            case PaymentModel.PWYW:
                switch (responseType) {
                    case ResponseType.Text:
                        const textPrice = artistInfo.pwywPrice.text;
                        if (
                            !textPrice ||
                            !textPrice.min ||
                            !textPrice.suggested ||
                            !textPrice.max
                        ) {
                            return null;
                        }
                        const textMarks: Mark[] = [
                            {
                                value: textPrice.min,
                                label: (
                                    <Typography
                                        sx={{ color: theme.palette.secondary.main }}
                                    >
                                        ${textPrice.min}
                                    </Typography>
                                ),
                            },
                            {
                                value: textPrice.suggested,
                                label: (
                                    <Typography
                                        sx={{ color: theme.palette.secondary.main }}
                                    >
                                        ${textPrice.suggested}
                                    </Typography>
                                ),
                            },
                            {
                                value: textPrice.max,
                                label: (
                                    <Typography
                                        sx={{ color: theme.palette.secondary.main }}
                                    >
                                        ${textPrice.max}
                                    </Typography>
                                ),
                            },
                        ];
                        return (
                            <Stack alignItems={'center'} sx={{ minWidth: '300px' }}>
                                <Stack direction='row' alignItems='center' spacing={1}>
                                    <Typography
                                        variant='subtitle1'
                                        fontFamily='LemonMilk'
                                    >
                                        Select Your Price
                                    </Typography>
                                    <Icon
                                        fontSize='inherit'
                                        name='cost'
                                        color='primary'
                                    />
                                </Stack>
                                <Slider
                                    value={selectedCost || textPrice.suggested}
                                    onChange={handleSliderChange}
                                    min={textPrice.min}
                                    max={textPrice.max}
                                    step={1}
                                    valueLabelDisplay='auto'
                                    marks={textMarks}
                                    sx={{ width: '100%' }}
                                />
                            </Stack>
                        );
                    case ResponseType.Video:
                        const videoPrice = artistInfo.pwywPrice.video;
                        if (
                            !videoPrice ||
                            !videoPrice.min ||
                            !videoPrice.suggested ||
                            !videoPrice.max
                        ) {
                            return null;
                        }
                        const marks: Mark[] = [
                            {
                                value: videoPrice.min,
                                label: (
                                    <Typography
                                        sx={{ color: theme.palette.secondary.main }}
                                    >
                                        ${videoPrice.min}
                                    </Typography>
                                ),
                            },
                            {
                                value: videoPrice.suggested,
                                label: (
                                    <Typography
                                        sx={{ color: theme.palette.secondary.main }}
                                    >
                                        ${videoPrice.suggested}
                                    </Typography>
                                ),
                            },
                            {
                                value: videoPrice.max,
                                label: (
                                    <Typography
                                        sx={{ color: theme.palette.secondary.main }}
                                    >
                                        ${videoPrice.max}
                                    </Typography>
                                ),
                            },
                        ];
                        return (
                            <Stack alignItems={'center'} sx={{ minWidth: '300px' }}>
                                <Stack direction='row' alignItems='center' spacing={1}>
                                    <Typography
                                        variant='subtitle1'
                                        fontFamily='LemonMilk'
                                    >
                                        Select Your Price
                                    </Typography>
                                    <Icon
                                        fontSize='inherit'
                                        name='cost'
                                        color='primary'
                                    />
                                </Stack>
                                <Slider
                                    value={selectedCost || videoPrice.suggested}
                                    onChange={handleSliderChange}
                                    min={videoPrice.min}
                                    max={videoPrice.max}
                                    step={1}
                                    valueLabelDisplay='auto'
                                    marks={marks}
                                    sx={{ width: '100%' }}
                                />
                            </Stack>
                        );
                }
        }
    }
};

export default CostCard;
